
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { SearchWidget } from './SearchWidget';
import { getRandomTravelImage, getRandomImageForTerm } from '../services/imageService';
import type { SearchQuery, ApiLogEntry } from '../types';
import { useLocalization } from '../hooks/useLocalization';
// DB-FIRST: Destinations loaded from API only (no localStorage)

import { usePageContent } from '../hooks/usePageContent';

interface HolidaysPageProps {
    logApiCall: (log: ApiLogEntry) => void;
    isSettingsReady: boolean;
}

const ServiceCard: React.FC<{ title: string, desc: string, image: string }> = ({ title, desc, image }) => (
    <div className="relative overflow-hidden rounded-2xl group h-80 cursor-pointer shadow-lg">
        <img
            src={image}
            alt={title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent flex flex-col justify-end p-6 transition-all duration-300">
            <h3 className="text-2xl font-bold text-white mb-2 translate-y-2 group-hover:translate-y-0 transition-transform duration-300">{title}</h3>
            <p className="text-gray-200 text-sm opacity-0 group-hover:opacity-100 translate-y-4 group-hover:translate-y-0 transition-all duration-300 delay-100">{desc}</p>
        </div>
    </div>
);

export const HolidaysPage: React.FC<HolidaysPageProps> = ({ logApiCall, isSettingsReady }) => {
    const { t, language } = useLocalization();
    const { content: cmsContent } = usePageContent('holidays', language);
    const navigate = useNavigate();
    const { state } = useLocation();

    // Default placeholders while loading
    const [heroImage, setHeroImage] = useState('https://images.unsplash.com/photo-1507525428034-b723cf961d3e?q=80&w=2073&auto=format&fit=crop');
    const [packages, setPackages] = useState<any[]>([]);

    const [serviceImages, setServiceImages] = useState({
        flights: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?q=80&w=2074&auto=format&fit=crop',
        hotels: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?q=80&w=2070&auto=format&fit=crop',
        transfers: 'https://images.unsplash.com/photo-1570125914364-FC370142d716?q=80&w=2070&auto=format&fit=crop'
    });

    const [whyChooseUsBg, setWhyChooseUsBg] = useState('https://images.unsplash.com/photo-1469474968028-56623f02e42e?q=80&w=2074&auto=format&fit=crop');

    useEffect(() => {
        if (cmsContent?.hero?.image) {
            setHeroImage(cmsContent.hero.image);
        }
    }, [cmsContent]);

    useEffect(() => {
        const loadContent = async () => {
            // Hero Image - Luxury Resort Beach
            if (!cmsContent?.hero?.image) {
                const bg = await getRandomImageForTerm("luxury holiday resort beach summer paradise");
                if (bg) setHeroImage(bg);
            }

            // Service Images with specific themes
            const flightImg = await getRandomImageForTerm("commercial airplane flying sky window view");
            const hotelImg = await getRandomImageForTerm("luxury hotel pool exterior modern architecture");
            const transferImg = await getRandomImageForTerm("luxury private car chauffeur airport transfer");

            setServiceImages(prev => ({
                flights: flightImg || prev.flights,
                hotels: hotelImg || prev.hotels,
                transfers: transferImg || prev.transfers
            }));

            // Why Choose Us Background
            const whyBg = await getRandomImageForTerm("adventure travel landscape panoramic nature hiking");
            if (whyBg) setWhyChooseUsBg(whyBg);

            // DB-FIRST: Fetch destinations from API (MongoDB)
            let dests: any[] = [];
            try {
                const res = await fetch('/api/destinations?active=true');
                const data = await res.json();
                if (data.success && data.destinations) {
                    dests = data.destinations.slice(0, 4).map((d: any) => ({
                        id: d._id,
                        destinationId: d.tourvisioId || d._id,
                        slug: d.slug,
                        title: d.name,
                        imageUrl: d.imageUrl || d.heroImage || '',
                        price: d.price || '',
                        type: d.type?.toString() || 'Flight + Hotel',
                        isActive: d.isActive
                    }));
                }
            } catch (e) {
                console.warn('[HolidaysPage] Failed to fetch destinations from API:', e);
            }
            const enhancedPackages = await Promise.all(dests.map(async (pkg) => {
                const dynamicImage = await getRandomImageForTerm(pkg.title + " travel landmark scenery");
                return {
                    ...pkg,
                    imageUrl: dynamicImage || pkg.imageUrl
                };
            }));

            setPackages(enhancedPackages);
        };
        loadContent();
    }, [cmsContent]);

    const handleSearch = (query: SearchQuery) => {
        navigate('/results', { state: { searchQuery: query, from: '/holidays' } });
    };

    return (
        <div className="min-h-screen bg-gray-50 font-sans">
            {/* Hero Section */}
            <div className="relative h-[80vh] min-h-[600px] w-full overflow-hidden">
                <div className="absolute inset-0">
                    <img
                        src={heroImage}
                        className="w-full h-full object-cover animate-slow-zoom"
                        alt="Holidays Background"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-transparent"></div>
                </div>

                <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-center pb-20">
                    <div className="max-w-3xl animate-fade-in-up">
                        <h1 className="text-5xl md:text-7xl font-black text-white mb-6 leading-tight tracking-tight">
                            {cmsContent?.hero?.title || t('holidays_hero_title')}
                        </h1>
                        <p className="text-xl md:text-2xl text-gray-200 mb-10 font-light max-w-2xl border-l-4 border-amber-500 pl-6">
                            {cmsContent?.hero?.subtitle || t('holidays_hero_subtitle')}
                        </p>
                    </div>
                </div>
            </div>

            {/* Search Widget - Floating Overlap */}
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-20 -mt-32 mb-20 animate-fade-in-up delay-200">
                <div className="shadow-2xl rounded-3xl">
                    <SearchWidget
                        onSearch={handleSearch}
                        logApiCall={logApiCall}
                        isSettingsReady={isSettingsReady}
                        initialQuery={state?.restoreQuery}
                    />
                </div>
            </div>

            {/* Services Section */}
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 mb-24">
                <div className="text-center mb-16">
                    <span className="text-amber-500 font-bold uppercase tracking-wider text-xs">LumiTours Premium</span>
                    <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mt-2">{t('our_services')}</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    <ServiceCard
                        title={t('service_flights_title')}
                        desc={t('service_flights_desc')}
                        image={serviceImages.flights}
                    />
                    <ServiceCard
                        title={t('service_hotels_title')}
                        desc={t('service_hotels_desc')}
                        image={serviceImages.hotels}
                    />
                    <ServiceCard
                        title={t('service_transfers_title')}
                        desc={t('service_transfers_desc')}
                        image={serviceImages.transfers}
                    />
                </div>
            </div>

            {/* Trending Packages (Modern Horizontal Layout) */}
            <div className="bg-white py-24 border-t border-gray-100">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-end mb-12">
                        <div>
                            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900">{t('trending_packages')}</h2>
                            <p className="text-gray-500 mt-2">Curated for the season</p>
                        </div>
                        <button className="hidden md:block text-amber-600 font-bold hover:text-amber-700 hover:underline transition-all">
                            View All Destinations &rarr;
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {packages.map((pkg, idx) => (
                            <div key={idx} className="bg-white border border-gray-100 rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300 group cursor-pointer">
                                <div className="h-48 overflow-hidden relative">
                                    <img src={pkg.imageUrl} alt={pkg.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                                    <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm px-2 py-1 rounded text-xs font-bold text-gray-900 uppercase">
                                        {pkg.type}
                                    </div>
                                </div>
                                <div className="p-5">
                                    <div className="flex justify-between items-start mb-2">
                                        <h3 className="font-bold text-lg text-gray-900 leading-tight">{pkg.title}</h3>
                                    </div>
                                    <div className="flex items-center text-gray-500 text-xs mb-4 space-x-2">
                                        <span>7 Nights</span>
                                        <span>•</span>
                                        <span>Flight Included</span>
                                    </div>
                                    <div className="flex justify-between items-center border-t border-gray-50 pt-4">
                                        <div>
                                            <span className="text-xs text-gray-400 block uppercase">From</span>
                                            <span className="text-amber-600 font-extrabold text-lg">{pkg.price}</span>
                                        </div>
                                        <button className="bg-gray-900 text-white px-4 py-2 rounded-lg text-xs font-bold hover:bg-gray-800 transition-colors">
                                            {t('view_package')}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Why Choose Us */}
            <div className="bg-gray-900 text-white py-20 relative overflow-hidden">
                <div className="absolute inset-0 opacity-20">
                    <img src={whyChooseUsBg} className="w-full h-full object-cover grayscale" alt="Adventure Background" />
                </div>
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="max-w-4xl mx-auto text-center">
                        <h2 className="text-3xl font-bold mb-12">{t('why_choose_us')}</h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                            <div className="p-6 bg-white/5 backdrop-blur-md rounded-2xl border border-white/10">
                                <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-6 text-white">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                </div>
                                <p className="text-gray-300 text-sm leading-relaxed">{t('why_desc_1')}</p>
                            </div>
                            <div className="p-6 bg-white/5 backdrop-blur-md rounded-2xl border border-white/10">
                                <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-6 text-white">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                                </div>
                                <p className="text-gray-300 text-sm leading-relaxed">{t('why_desc_2')}</p>
                            </div>
                            <div className="p-6 bg-white/5 backdrop-blur-md rounded-2xl border border-white/10">
                                <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-6 text-white">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                </div>
                                <p className="text-gray-300 text-sm leading-relaxed">{t('why_desc_3')}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
